import { Input, InputProps } from '@chakra-ui/react';
import { forwardRef, ReactNode, useId } from 'react';
import {
  B2B_FIELD_CONTROL_BORDER_RADIUS,
  B2B_FIELD_CONTROL_BORDER_WIDTH,
  B2B_FIELD_CONTROL_HEIGHT_PX,
  B2B_FIELD_CONTROL_MAX_WIDTH,
  B2B_FIELD_CONTROL_PADDING_X,
  B2B_FIELD_CONTROL_PADDING_Y,
} from '../b2bFormControlTokens';
import { B2bFieldLayout } from '../B2bFieldLayout';
import { b2bFieldFocusVisible } from 'styles/colors';

export type FieldTextProps = Omit<InputProps, 'id'> & {
  id?: string;
  label: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  /** Form block direction — RTL aligns placeholder and text to the design (Arabic). */
  layoutDir?: 'ltr' | 'rtl';
};

const isForcedLtrInput = (type: string | undefined, inputMode: string | undefined) =>
  type === 'email' ||
  type === 'url' ||
  type === 'tel' ||
  type === 'number' ||
  inputMode === 'numeric' ||
  inputMode === 'decimal';

/** Chakra `Input` with `forwardRef` for React Hook Form: `{...register('name')}`. */
export const FieldText = forwardRef<HTMLInputElement, FieldTextProps>(
  (
    {
      id: idProp,
      label,
      hint,
      error,
      isDisabled,
      isRequired,
      isReadOnly,
      isInvalid,
      layoutDir,
      ...inputProps
    },
    ref,
  ) => {
    const autoId = useId();
    const fieldId = idProp ?? `b2b-field-text-${autoId}`;
    const { type, inputMode, ...restInputProps } = inputProps;

    const forcedLtr = isForcedLtrInput(type, inputMode);

    let inputDir: 'ltr' | 'rtl' | 'auto';
    let textAlign: 'left' | 'right' | 'start' | 'end';

    if (layoutDir === 'rtl') {
      if (forcedLtr) {
        inputDir = 'ltr';
        /** Keep bidi for URL/email, but align placeholder and value like other RTL fields. */
        textAlign = 'right';
      } else {
        inputDir = 'rtl';
        textAlign = 'right';
      }
    } else if (layoutDir === 'ltr') {
      inputDir = forcedLtr ? 'ltr' : 'ltr';
      textAlign = 'start';
    } else {
      inputDir = forcedLtr ? 'ltr' : 'auto';
      textAlign = 'start';
    }

    const placeholderAlign =
      layoutDir === 'rtl'
        ? ('right' as const)
        : layoutDir === 'ltr'
        ? ('left' as const)
        : undefined;
    const placeholderSx =
      placeholderAlign != null
        ? { color: 'dark.D400', textAlign: placeholderAlign }
        : { color: 'dark.D400' };

    return (
      <B2bFieldLayout
        fieldId={fieldId}
        label={label}
        hint={hint}
        error={error}
        isDisabled={isDisabled}
        isRequired={isRequired}
        isReadOnly={isReadOnly}
        isInvalid={isInvalid}
      >
        <Input
          id={fieldId}
          ref={ref}
          type={type}
          inputMode={inputMode}
          dir={inputDir}
          textAlign={textAlign}
          w='full'
          maxW={B2B_FIELD_CONTROL_MAX_WIDTH}
          display='block'
          h={B2B_FIELD_CONTROL_HEIGHT_PX}
          minH={B2B_FIELD_CONTROL_HEIGHT_PX}
          px={B2B_FIELD_CONTROL_PADDING_X}
          py={B2B_FIELD_CONTROL_PADDING_Y}
          fontSize='md'
          lineHeight='1.25'
          borderRadius={B2B_FIELD_CONTROL_BORDER_RADIUS}
          borderWidth={B2B_FIELD_CONTROL_BORDER_WIDTH}
          borderColor='dark.D150'
          _hover={{ borderColor: 'dark.D200' }}
          _placeholder={placeholderSx}
          _focusVisible={{
            ...b2bFieldFocusVisible,
          }}
          {...restInputProps}
        />
      </B2bFieldLayout>
    );
  },
);

FieldText.displayName = 'FieldText';
